import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { LucideIcon } from "lucide-react";

export interface ResourceLink {
  title: string;
  description: string;
  href: string;
  icon?: LucideIcon;
}

interface ResourcesGridProps {
  variant: "bbq";
  title?: string;
  subtitle?: string;
  links: ResourceLink[];
  columns?: 3 | 4 | 6;
}

const variantStyles = {
  bbq: {
    section: "py-16 bg-[#2a2520]",
    titleLabel: "text-orange-500 font-bold uppercase tracking-widest text-sm",
    title: "text-3xl md:text-4xl font-black text-[#f5e6d3] mt-2",
    card: "bg-[#1a1a1a] p-6 border-l-4 border-orange-500 hover:bg-[#252525] transition-colors group",
    cardTitle: "text-lg font-bold text-[#f5e6d3] group-hover:text-orange-400 transition-colors mb-2",
    cardDescription: "text-[#f5e6d3]/60 text-sm",
    iconContainer: "w-12 h-12 bg-orange-500/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-orange-500/20 transition-colors",
    iconColor: "text-orange-500",
  },
};

const ResourcesGrid = ({
  variant,
  title = "Helpful Guides & Info",
  subtitle,
  links,
  columns = 4,
}: ResourcesGridProps) => {
  const styles = variantStyles[variant];
  const shouldReduceMotion = useReducedMotion();

  const gridCols = {
    3: "lg:grid-cols-3",
    4: "lg:grid-cols-4",
    6: "lg:grid-cols-3",
  };

  return (
    <section className={styles.section}>
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-10"
          initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className={styles.titleLabel}>Resources</span>
          <h2 className={styles.title}>
            {title}
          </h2>
          {subtitle && (
            <p className="text-gray-600 mt-2">
              {subtitle}
            </p>
          )}
        </motion.div>

        <div className={`grid md:grid-cols-2 ${gridCols[columns]} gap-6 ${columns === 3 || columns === 6 ? "max-w-5xl mx-auto" : ""}`}>
          {links.map((link, index) => (
            <motion.div
              key={link.href}
              initial={shouldReduceMotion ? {} : { opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={shouldReduceMotion ? {} : { 
                y: -6,
                transition: { duration: 0.2, ease: "easeOut" }
              }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{
                duration: 0.5,
                delay: shouldReduceMotion ? 0 : index * 0.1,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              <Link to={link.href} className={styles.card + " block h-full"}>
                {link.icon && (
                  <motion.div 
                    className={styles.iconContainer}
                    whileHover={shouldReduceMotion ? {} : { 
                      scale: 1.1,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <link.icon className={`w-6 h-6 ${styles.iconColor}`} />
                  </motion.div>
                )}
                <h3 className={styles.cardTitle}>{link.title}</h3>
                <p className={styles.cardDescription}>{link.description}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ResourcesGrid;
